import type { AppRuntime } from "./api";

export type RuntimeActionKind =
  | "deploy" // initialer Pull & Start (noch nie deployed)
  | "redeploy" // aktuellen Tag erneut ziehen & starten
  | "update" // ein Update anwenden (Pull + Neustart)
  | "pull" // #103: nur herunterladen, Container nicht neu starten
  | "start"
  | "stop"
  | "restart"
  | "rollback"
  | "checkUpdate"
  | "logs";

/** Visuelle Gruppierung der Aktionsleiste (#103): Container-Steuerung vs. Image/Updates vs. Diagnose. */
export type RuntimeActionGroup = "container" | "image" | "diagnostics";

export interface ActionDescriptor {
  kind: RuntimeActionKind;
  /** Hervorgehoben als empfohlener nächster Schritt. Höchstens eine pro Leiste. */
  primary: boolean;
  group: RuntimeActionGroup;
}

export interface RuntimeActionInput {
  runtime: Pick<AppRuntime, "observed_state" | "pinned_digest"> | null;
  /** Es existiert ein previous_digest zum Zurückrollen. */
  hasPrevious: boolean;
  /** Ein neueres Image ist in der Registry verfügbar. */
  updateAvailable: boolean;
}

export function availableActions(input: RuntimeActionInput): ActionDescriptor[] {
  const { runtime, hasPrevious, updateAvailable } = input;

  // Nie deployed: einzig sinnvolle Aktion ist der initiale Deploy. Das gilt fuer
  // eine fehlende Runtime-Zeile ODER einen fehlenden pinned_digest, solange die
  // App noch im unberuehrten Default-Zustand "unknown" ist. Lief die App schon
  // einmal (jeder andere observed_state), behaelt sie ihren vollen Aktionssatz —
  // auch wenn der pinned_digest verloren ging (#2: sonst laesst sich ein failed-
  // Container mit verlorenem Digest nie stoppen).
  const pulled = runtime != null && runtime.pinned_digest != null;
  if (runtime == null || (!pulled && runtime.observed_state === "unknown")) {
    return [{ kind: "deploy", primary: true, group: "image" }];
  }

  const state = runtime.observed_state;

  // Transitional: eine Operation läuft bereits — nur Logs anbieten.
  if (state === "pulling" || state === "starting") {
    return [{ kind: "logs", primary: false, group: "diagnostics" }];
  }

  const running = state === "healthy" || state === "unhealthy";
  const out: Omit<ActionDescriptor, "primary">[] = [];

  if (running) {
    out.push({ kind: "stop", group: "container" });
    out.push({ kind: "restart", group: "container" });
  } else {
    // stopped | failed | unknown
    out.push({ kind: "start", group: "container" });
    // #2: `failed`/`unknown` may still have a running container (a healthcheck
    // timeout sets `failed` while `state=running`), so Stop must be offered —
    // composeDown is idempotent. `stopped` is genuinely down → no Stop.
    if (state === "failed" || state === "unknown") {
      out.push({ kind: "stop", group: "container" });
    }
    out.push({ kind: "redeploy", group: "image" });
    if (hasPrevious) out.push({ kind: "rollback", group: "image" });
  }

  // #103 (Docker-Desktop-Modell): Steht ein Update bereit, erst „herunterladen"
  // (Pull ohne Neustart) anbieten, dann „anwenden" (Deploy). Pull ist der
  // empfohlene erste Schritt.
  if (updateAvailable) {
    out.push({ kind: "pull", group: "image" });
    out.push({ kind: "update", group: "image" });
  }
  out.push({ kind: "checkUpdate", group: "image" });
  out.push({ kind: "logs", group: "diagnostics" });

  // Genau eine primäre Aktion: update-download > stop(läuft) > start(gestoppt).
  const primaryKind: RuntimeActionKind = updateAvailable ? "pull" : running ? "stop" : "start";
  return out.map((a) => ({ ...a, primary: a.kind === primaryKind }));
}
